/**
 * UI bootstrap — Neovaa Configurator v4.
 * Builds the product-page widget: 3D viewer + sticky buy rail with the
 * Shape / Fabric / Legs steps, gallery, and "all shapes" modal.
 */

import { Signal } from "@/state/Store";
import { computePrice, formatPrice, priceBreakdown } from "@/state/Config";
import { createCheckout, goToCheckout, currentSlug } from "@/state/checkout";
import { shopifyActive, hydrateLivePrice, buyShopify } from "@/state/commerce";
import { pushURL, shareURL } from "@/state/URLState";
import { wireCarousel } from "./Carousel";
import { formeIcon } from "./formeIcons";
import { showToast } from "./Toast";
import { trackEvent } from "@/analytics/Tracker";
import type { Engine } from "@/engine/Engine";
import type { ConfigState, ProductConfig, FabricCategory } from "@/types";

export interface AppOptions {
  product: ProductConfig;
  state: Signal<ConfigState>;
  engine: Engine;
  isMobile: boolean;
}

const CATEGORIES: { id: FabricCategory; label: string }[] = [
  { id: "mat", label: "Matte" },
  { id: "satin", label: "Satin" },
  { id: "cuir", label: "Leather" },
];

export function bootstrapUI(opts: AppOptions): void {
  const { product, state, engine } = opts;

  renderRailHead(product);
  renderSteps(product, state, engine);
  renderGallery();
  renderModal(product, state, engine);
  wireViewerTools(engine, state);
  wireActions(product, state);

  sync(product, state.get(), engine);
  state.subscribe((s) => {
    sync(product, s, engine);
    pushURL(s);
  });
}

/* ── helpers ─────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

/** A picture tile with a label and a thin selection underline. */
function tile(opts: {
  selectKey: "forme" | "fabric" | "leg";
  id: string;
  label: string;
  color?: string;
  image?: string;
  variant?: "forme" | "swatch";
}): HTMLButtonElement {
  const btn = el("button", "tile");
  btn.type = "button";
  if (opts.variant === "forme") btn.classList.add("tile--forme");
  btn.dataset[opts.selectKey] = opts.id;
  // Mirror data-forme as data-layout so the E2E suite can drive the layout
  // by its human name (?layout=medium / [data-layout="medium"]).
  if (opts.selectKey === "forme") btn.dataset.layout = opts.id;
  btn.setAttribute("aria-label", opts.label);

  const img = el("span", "tile__img");
  if (opts.color) img.style.setProperty("--tile-color", opts.color);
  if (opts.image) img.style.backgroundImage = `url(${opts.image})`;
  btn.appendChild(img);

  btn.appendChild(el("span", "tile__label", opts.label));
  btn.appendChild(el("span", "tile__sel"));
  return btn;
}

/* ── rail head ───────────────────────────────────────────── */

function renderRailHead(product: ProductConfig): void {
  const name = document.getElementById("rail-name");
  if (name) name.textContent = product.productLabel;

  const rating = document.getElementById("rail-rating");
  if (rating && product.rating) {
    const stars = el("span", "stars", "★★★★★");
    rating.appendChild(stars);
    rating.appendChild(
      document.createTextNode(
        `  ${product.rating} · ${product.reviewCount ?? 0} reviews`,
      ),
    );
  }

  const desc = document.getElementById("rail-desc");
  if (desc) desc.textContent = product.description;

  // Price-detail toggle
  const toggle = document.getElementById("price-toggle");
  const detail = document.getElementById("price-detail");
  toggle?.addEventListener("click", () => {
    const open = detail?.classList.toggle("hidden") === false;
    toggle.setAttribute("aria-expanded", String(open));
  });
}

/* ── steps ───────────────────────────────────────────────── */

function step(n: number, title: string, hint?: string): { root: HTMLElement; body: HTMLElement } {
  const root = el("section", "step");
  root.appendChild(el("div", "step__label", `Step ${n} · ${title}`));
  if (hint) root.appendChild(el("div", "step__hint", hint));
  const body = el("div", "step__body");
  root.appendChild(body);
  return { root, body };
}

function renderSteps(product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  const steps = document.getElementById("steps");
  if (!steps) return;
  steps.innerHTML = "";

  // Step 1 — Shape (carousel + "see all shapes")
  const s1 = step(1, "Shape", "Carousel — swipe or use arrows");
  const carousel = el("div", "carousel");
  const prev = el("button", "carousel__arrow");
  prev.type = "button";
  prev.textContent = "‹";
  prev.setAttribute("aria-label", "Previous");
  const track = el("div", "carousel__track");
  const next = el("button", "carousel__arrow");
  next.type = "button";
  next.textContent = "›";
  next.setAttribute("aria-label", "Next");
  for (const forme of product.formes.filter((f) => f.featured)) {
    const t = tile({
      selectKey: "forme",
      id: forme.id,
      label: forme.label,
      image: forme.image ?? formeIcon(forme.id),
      variant: "forme",
    });
    t.addEventListener("click", () => selectForme(forme.id, product, state, engine));
    track.appendChild(t);
  }
  carousel.append(prev, track, next);
  s1.body.appendChild(carousel);
  const more = el("button", "step__more", "See all shapes →");
  more.type = "button";
  more.id = "open-formes";
  s1.body.appendChild(more);
  steps.appendChild(s1.root);
  wireCarousel(track, prev as HTMLButtonElement, next as HTMLButtonElement);

  // Step 2 — Fabric (sub-tabs + swatch grid)
  const s2 = step(2, "Fabric");
  const subtabs = el("div", "subtabs");
  const grid2 = el("div", "swatch-grid");
  let activeCat: FabricCategory =
    product.fabrics.find((f) => f.id === state.get().fabricId)?.category ?? "mat";

  const renderGrid = () => {
    grid2.innerHTML = "";
    for (const fab of product.fabrics.filter((f) => f.category === activeCat)) {
      const t = tile({ selectKey: "fabric", id: fab.id, label: fab.label, color: fab.color, image: fab.image });
      t.addEventListener("click", () => selectFabric(fab.id, product, state, engine));
      grid2.appendChild(t);
    }
    sync(product, state.get(), engine);
  };
  for (const cat of CATEGORIES) {
    const tab = el("button", "subtab");
    tab.type = "button";
    tab.dataset.cat = cat.id;
    tab.appendChild(el("span", undefined, cat.label));
    tab.appendChild(el("span", "subtab__sel"));
    if (cat.id === activeCat) tab.classList.add("is-active");
    tab.addEventListener("click", () => {
      activeCat = cat.id;
      subtabs.querySelectorAll(".subtab").forEach((s) =>
        s.classList.toggle("is-active", (s as HTMLElement).dataset.cat === activeCat),
      );
      renderGrid();
    });
    subtabs.appendChild(tab);
  }
  s2.body.append(subtabs, grid2);
  steps.appendChild(s2.root);
  renderGrid();

  // Step 3 — Legs (swatch grid)
  const s3 = step(3, "Legs");
  const grid3 = el("div", "swatch-grid");
  for (const leg of product.legs) {
    const t = tile({ selectKey: "leg", id: leg.id, label: leg.label, color: leg.color, image: leg.image });
    t.addEventListener("click", () => selectLeg(leg.id, product, state, engine));
    grid3.appendChild(t);
  }
  s3.body.appendChild(grid3);
  steps.appendChild(s3.root);
}

/* ── gallery ─────────────────────────────────────────────── */

function renderGallery(): void {
  const track = document.getElementById("gallery-track");
  const prev = document.getElementById("gallery-prev") as HTMLButtonElement | null;
  const next = document.getElementById("gallery-next") as HTMLButtonElement | null;
  if (!track || !prev || !next) return;
  track.innerHTML = "";
  for (let i = 0; i < 6; i++) {
    track.appendChild(el("div", "gallery__item"));
  }
  wireCarousel(track, prev, next);
}

/* ── modal ───────────────────────────────────────────────── */

function renderModal(product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  const modal = document.getElementById("forme-modal");
  const grid = document.getElementById("modal-grid");
  if (!modal || !grid) return;
  grid.innerHTML = "";
  for (const forme of product.formes) {
    const t = tile({
      selectKey: "forme",
      id: forme.id,
      label: forme.label,
      image: forme.image ?? formeIcon(forme.id),
      variant: "forme",
    });
    t.addEventListener("click", () => {
      selectForme(forme.id, product, state, engine);
      closeModal();
    });
    grid.appendChild(t);
  }

  const openModal = () => modal.classList.remove("hidden");
  const closeModal = () => modal.classList.add("hidden");

  document.getElementById("open-formes")?.addEventListener("click", openModal);
  document.getElementById("modal-close")?.addEventListener("click", closeModal);
  document.getElementById("modal-backdrop")?.addEventListener("click", closeModal);
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeModal();
  });
}

/* ── viewer tools ────────────────────────────────────────── */

function wireViewerTools(engine: Engine, state: Signal<ConfigState>): void {
  const viewer = document.getElementById("viewer");
  const dims = el("div", "viewer__dims");
  dims.style.cssText =
    "position:absolute;left:12px;bottom:12px;font-size:11px;color:#6f6a60;" +
    "background:#fff;border:1px solid #e6e3dd;border-radius:2px;padding:4px 8px;display:none;";
  viewer?.appendChild(dims);

  const refreshDims = () => {
    const d = engine.getDimensions();
    if (d) {
      dims.textContent = `${Math.round(d.w * 100)} × ${Math.round(d.d * 100)} × ${Math.round(
        d.h * 100,
      )} cm`;
    }
  };
  // keep dims fresh as the model changes
  state.subscribe(() => {
    if (dims.style.display !== "none") setTimeout(refreshDims, 60);
  });

  document.querySelectorAll<HTMLButtonElement>(".viewer__tool").forEach((btn) => {
    btn.addEventListener("click", () => {
      const tool = btn.dataset.tool;
      if (tool === "dimensions") {
        const on = btn.classList.toggle("is-active");
        dims.style.display = on ? "block" : "none";
        if (on) refreshDims();
      } else if (tool === "animation") {
        btn.classList.toggle("is-active");
        showToast("Animation: the sofa unfolds (demo)");
        trackEvent("animation_toggle", {});
      } else if (tool === "ar") {
        showToast("AR: scan with your phone (demo)");
        trackEvent("ar_click", {});
      } else if (tool === "fullscreen") {
        if (document.fullscreenElement) {
          void document.exitFullscreen();
        } else {
          void viewer?.requestFullscreen?.().catch(() => showToast("Fullscreen unavailable"));
        }
      }
    });
  });
}

/* ── actions: CTA / share ────────────────────────────────── */

function wireActions(product: ProductConfig, state: Signal<ConfigState>): void {
  document.getElementById("cta")?.addEventListener("click", () => {
    const total = computePrice(product, state.get());
    trackEvent("add_to_cart", { productId: product.productId, ...state.get(), price: total });
    // Shopify-sourced: add the real pieces to the normal Shopify cart.
    if (shopifyActive(product)) {
      const out = buyShopify(product, state.get());
      if (out.status === "blocked") showToast(out.message);
      else showToast(out.status === "navigating" ? "Opening cart..." : "Added to cart");
      return;
    }
    // Made-to-order: the proven draft-order checkout path.
    const slug = currentSlug();
    if (!slug) {
      showToast(`${product.productLabel} added, ${formatPrice(total, product.currency)}`);
      return;
    }
    showToast("Opening checkout...");
    createCheckout(slug, state.get())
      .then((r) => goToCheckout(r.checkoutUrl))
      .catch((e) => showToast(e?.message || "Checkout unavailable, please try again"));
  });

  document.getElementById("act-share")?.addEventListener("click", async () => {
    const url = shareURL(state.get());
    trackEvent("share_click", {});
    try {
      await navigator.clipboard.writeText(url);
      showToast("Configuration link copied");
    } catch {
      showToast("Sharing: copy the URL from the address bar");
    }
  });
}

/* ── state changes ───────────────────────────────────────── */

function selectForme(id: string, product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  if (state.get().formeId === id) return;
  trackEvent("forme_change", { from: state.get().formeId, to: id });
  state.set({ ...state.get(), formeId: id });
  void engine.rebuildAssembly(product, state.get());
}

function selectFabric(id: string, product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  if (state.get().fabricId === id) return;
  trackEvent("fabric_change", { from: state.get().fabricId, to: id });
  state.set({ ...state.get(), fabricId: id });
  const fabric = product.fabrics.find((f) => f.id === id);
  if (fabric) engine.applyFabric(fabric);
}

function selectLeg(id: string, product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  if (state.get().legId === id) return;
  trackEvent("leg_change", { from: state.get().legId, to: id });
  state.set({ ...state.get(), legId: id });
  const leg = product.legs.find((l) => l.id === id);
  if (leg) engine.applyLeg(leg);
}

/* ── sync DOM to state ───────────────────────────────────── */

function sync(product: ProductConfig, s: ConfigState, _engine: Engine): void {
  const total = computePrice(product, s);

  const priceEl = document.getElementById("rail-price");
  if (priceEl) priceEl.textContent = formatPrice(total, product.currency);

  const detail = document.getElementById("price-detail");
  if (detail) {
    detail.innerHTML = "";
    for (const line of priceBreakdown(product, s)) {
      const row = el("div", "rail__price-line");
      row.appendChild(el("span", undefined, line.label));
      row.appendChild(el("span", undefined, formatPrice(line.amount, product.currency)));
      detail.appendChild(row);
    }
    const totalRow = el("div", "rail__price-line is-total");
    totalRow.appendChild(el("span", undefined, "Total"));
    totalRow.appendChild(el("span", undefined, formatPrice(total, product.currency)));
    detail.appendChild(totalRow);
  }

  const lead = document.getElementById("rail-lead");
  if (lead) {
    const monthly = formatPrice(Math.round(total / 4), product.currency);
    lead.textContent = `${product.leadTime ?? ""} · or 4× ${monthly} interest-free`;
  }

  // Shopify-sourced: overlay the live store total once it arrives (the local price
  // above stays put until then, and remains if the store is unreachable).
  if (shopifyActive(product)) {
    hydrateLivePrice(currentSlug(), product, s, (live) => {
      if (!live) return;
      // Live total is in the STORE's currency (Shopify is the source of truth here).
      if (priceEl) priceEl.textContent = formatPrice(live.total, live.currency);
      if (lead) {
        const m = formatPrice(Math.round(live.total / 4), live.currency);
        lead.textContent = `${product.leadTime ?? ""} · or 4× ${m} interest-free`;
      }
    });
  }

  const forme = product.formes.find((f) => f.id === s.formeId);
  const fabric = product.fabrics.find((f) => f.id === s.fabricId);
  const leg = product.legs.find((l) => l.id === s.legId);
  const live = document.getElementById("cfg-live");
  if (live) {
    live.textContent = `Configuration: ${forme?.label ?? ""} · ${fabric?.label ?? ""} · ${
      leg?.label ?? ""
    } legs`;
  }

  // Selection state on tiles
  document.querySelectorAll<HTMLElement>("[data-forme]").forEach((n) =>
    n.classList.toggle("is-selected", n.dataset.forme === s.formeId),
  );
  document.querySelectorAll<HTMLElement>("[data-fabric]").forEach((n) =>
    n.classList.toggle("is-selected", n.dataset.fabric === s.fabricId),
  );
  document.querySelectorAll<HTMLElement>("[data-leg]").forEach((n) =>
    n.classList.toggle("is-selected", n.dataset.leg === s.legId),
  );

  // Gallery reflects the chosen fabric (placeholder visuals until CGI exists)
  if (fabric?.color) {
    document.querySelectorAll<HTMLElement>(".gallery__item").forEach((n) => {
      n.style.backgroundColor = fabric.color as string;
    });
  }
}
